import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Bundle, ServiceRequest } from 'fhir/r4';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { backendEndPointToken } from '../../app.config';
import { ErrorService } from '../error.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ResourceDataReviewComponent } from '../resource-data-review/resource-data-review.component';
import { FormFieldsSelectDataService } from '../form-fields-select-data.service';
import { UtilityService } from '../utility.service';
import { StateService } from '../state.service';
import { applyItemsRequiredRowsToActivityDefinition, extractItemsRequiredRows, ItemsRequiredRow } from '../activity-definition-items-required';
import { ItemsRequiredApplyChoice, ItemsRequiredApplyDialogComponent } from '../items-required-apply-dialog/items-required-apply-dialog.component';

@Component({
    selector: 'app-items-required-editor',
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        ResourceDataReviewComponent,
    ],
    templateUrl: './items-required-editor.component.html',
    styleUrl: './items-required-editor.component.scss'
})
export class ItemsRequiredEditorComponent {
    private readonly http = inject(HttpClient);
    private readonly backendApiBaseUrl = inject(backendEndPointToken);
    private readonly formFieldService = inject(FormFieldsSelectDataService);
    private readonly utilityService = inject(UtilityService);
    private readonly stateService = inject(StateService);
    private readonly dialog = inject(MatDialog);
    private readonly snackBar = inject(MatSnackBar);
    private readonly errorService = inject(ErrorService);

    private readonly dialogRef = inject(MatDialogRef<ItemsRequiredEditorComponent>, { optional: true });
    private readonly dialogData = inject<any>(MAT_DIALOG_DATA, { optional: true });

    serviceRequest: ServiceRequest | null = null;
    patientId: string | null = null;

    itemsRequiredForm: any[] | null = null;
    itemsRequiredLoading = false;
    itemsRequiredSource: 'instance' | 'default' | 'none' = 'none';
    itemsRequiredReadOnlyNote: string | null = null;
    serviceRequestCompletedNote: string | null = null;

    resolvedActivityDefinition: any | null = null;
    originalItemsRequiredRows: ItemsRequiredRow[] = [];
    itemsRequiredRows: ItemsRequiredRow[] = [];
    itemsRequiredDirty = false;

    private readonly __DELETE_EMPTY = Symbol('DELETE_EMPTY_FHIR_FIELD');

    private shouldPersistServiceRequestLocally(sr: any): boolean {
        // Only persist into local (patient-scoped) resources when we truly are in a patient context,
        // which in this app is represented by StateService.PatientResources.currentPatient.
        const currentPatientId = this.stateService.PatientResources.currentPatient.value?.actualResource?.id ?? null;
        if (!currentPatientId) return false;

        const subjectRef = (sr as any)?.subject?.reference?.toString?.() ?? '';
        const subjectId = subjectRef.startsWith('Patient/') ? subjectRef.split('/')[1] : null;
        return !!subjectId && subjectId === currentPatientId;
    }

    private persistServiceRequestState(sr: any): void {
        if (!sr) return;
        try {
            this.stateService.persistOrgWideResource(sr as any, 'saved');
            if (this.shouldPersistServiceRequestLocally(sr)) {
                this.stateService.persistLocalResource(sr as any, 'saved');
            }
        } catch {
            // best-effort; state updates should not block persistence
        }
    }

    ngOnInit(): void {
        const sr = (this.dialogData?.serviceRequest ?? null) as ServiceRequest | null;
        this.serviceRequest = sr;
        this.patientId = (this.dialogData?.patientId ?? null) as string | null;

        if (!this.serviceRequest) {
            this.itemsRequiredReadOnlyNote = 'ServiceRequest context is missing.';
            return;
        }

        if (this.isServiceRequestCompleted(this.serviceRequest)) {
            this.serviceRequestCompletedNote = 'This ServiceRequest is completed. Items Required is read-only.';
        }

        this.initItemsRequiredStep(this.serviceRequest);
    }

    private isServiceRequestCompleted(sr: any): boolean {
        const status = (sr?.status ?? '').toString().trim().toLowerCase();
        return status === 'completed';
    }

    get editingLockedByServiceRequestCompletion(): boolean {
        return this.isServiceRequestCompleted(this.serviceRequest);
    }

    close(): void {
        if (this.itemsRequiredDirty) {
            const confirmRef = this.dialog.open(ConfirmDialogComponent, {
                maxWidth: '520px',
                data: {
                    title: 'Discard changes?',
                    message: 'You have unsaved changes to Items Required. Close anyway?',
                    confirmText: 'Discard',
                    cancelText: 'Back',
                    icon: 'warning'
                }
            });
            confirmRef.afterClosed().subscribe((ok) => {
                if (ok) this.dialogRef?.close();
            });
            return;
        }

        this.dialogRef?.close();
    }

    changeItemsRequiredRowsToResourceDataFormat(next: ItemsRequiredRow[]): any[] {
        return (next ?? []).map((r) => ({
            items: (r as any)?.items?.coding ? (r as any).items.coding[0] : (r as any)?.items,
            quantity: (r as any)?.quantity
        }));
    }

    onItemsRequiredUpdatedFromReview(event: any): void {
        if (this.editingLockedByServiceRequestCompletion) return;
        const rows = (Array.isArray(event?.itemsRequired)
            ? event.itemsRequired
            : (Array.isArray(event) ? event : [])).map((r: any) => ({
                items: this.utilityService.toCodeableConcept(r?.items),
                quantity: r?.quantity
            } as ItemsRequiredRow));

        const sanitized = this.sanitizeItemsRequiredRows(rows);
        if (sanitized.removedCount > 0) {
            this.errorService.openandCloseError(`Removed ${sanitized.removedCount} invalid item(s) that were missing coding. Please delete and reselect from the list.`);
            this.snackBar.open(
                `Removed ${sanitized.removedCount} invalid item(s) (free-text). Please select from the list.`,
                'OK',
                { duration: 4000 }
            );
        }

        this.itemsRequiredRows = structuredClone(sanitized.rows);
        this.itemsRequiredDirty = JSON.stringify(this.itemsRequiredRows ?? []) !== JSON.stringify(this.originalItemsRequiredRows ?? []);
    }

    private sanitizeItemsRequiredRows(rows: ItemsRequiredRow[]): { rows: ItemsRequiredRow[]; removedCount: number } {
        const next: ItemsRequiredRow[] = [];
        let removed = 0;

        for (const row of rows ?? []) {
            const items: any = (row as any)?.items;
            const codings: any[] = Array.isArray(items?.coding) ? items.coding : [];
            const hasCode = codings.some((c) => !!(c?.code ?? '').toString().trim());
            if (!hasCode) {
                removed++;
                continue;
            }
            next.push(row);
        }

        return { rows: next, removedCount: removed };
    }

    confirmItemsUsed(): void {
        if (!this.serviceRequest) return;
        if (this.editingLockedByServiceRequestCompletion) {
            this.snackBar.open('This ServiceRequest is completed; editing is disabled.', 'OK', { duration: 3000 });
            return;
        }
        if (!this.itemsRequiredDirty) {
            this.snackBar.open('No changes to confirm.', 'OK', { duration: 2500 });
            return;
        }

        // When the Items Required came from an existing instance override already,
        // the default-vs-instance chooser is usually not what the user wants.
        // Re-apply as instance again so repeat edits actually stick.
        if (this.itemsRequiredSource === 'instance') {
            this.persistItemsRequiredForInstanceOverride$(this.serviceRequest as any).subscribe({
                next: () => {
                    this.itemsRequiredDirty = false;
                    this.originalItemsRequiredRows = structuredClone(this.itemsRequiredRows ?? []);
                    this.snackBar.open('Items Required confirmed for this request.', 'OK', { duration: 3000 });
                    this.dialogRef?.close(true);
                },
                error: (e) => {
                    console.error(e);
                    this.errorService.openandCloseError('Failed to confirm Items Required for this ServiceRequest.');
                }
            });
            return;
        }

        const srCode = this.getServiceRequestCode(this.serviceRequest);
        if (!srCode) {
            this.errorService.openandCloseError('Cannot apply Items Required changes: ServiceRequest code is missing.');
            return;
        }

        const dialogRef = this.dialog.open(ItemsRequiredApplyDialogComponent, {
            maxWidth: '680px',
            data: {
                title: 'Items required changed',
                message: 'Choose whether to apply this change as the new default for this service code, or apply only to this ServiceRequest.'
            }
        });

        dialogRef.afterClosed().subscribe((choice: ItemsRequiredApplyChoice) => {
            if (!choice || choice === 'cancel') return;

            const apply$ = choice === 'default'
                ? this.persistItemsRequiredAsDefaultForCodeAndPinSr$(this.serviceRequest as any, srCode)
                : this.persistItemsRequiredForInstanceOverride$(this.serviceRequest as any);

            apply$.subscribe({
                next: () => {
                    this.itemsRequiredDirty = false;
                    this.originalItemsRequiredRows = structuredClone(this.itemsRequiredRows ?? []);
                    this.snackBar.open(
                        choice === 'default'
                            ? 'Default Items Required updated for this code.'
                            : 'Items Required confirmed for this request.',
                        'OK',
                        { duration: 3000 }
                    );
                    this.dialogRef?.close(true);
                },
                error: (e) => {
                    console.error(e);
                    this.errorService.openandCloseError(
                        choice === 'default'
                            ? 'Failed to apply Items Required as default.'
                            : 'Failed to confirm Items Required for this ServiceRequest.'
                    );
                }
            });
        });
    }

    private initItemsRequiredStep(serviceRequest: ServiceRequest): void {
        this.itemsRequiredLoading = true;
        this.itemsRequiredSource = 'none';
        this.resolvedActivityDefinition = null;
        this.itemsRequiredDirty = false;
        this.itemsRequiredReadOnlyNote = null;

        this.formFieldService.getFormFieldSelectData('activityDefinition', 'itemsRequired').pipe(
            catchError(() => of([] as any[])),
            switchMap((itemsRequiredOptions) => {
                this.itemsRequiredForm = [
                    {
                        generalProperties: {
                            isArray: true,
                            isGroup: true,
                            fieldApiName: 'itemsRequired',
                            fieldName: 'Items Required',
                            fieldLabel: 'Items Required',
                            groupFieldsHint: 'Edit and confirm the materials/items used for this ServiceRequest. Quantity unit is preserved from the default configuration when possible.',
                            fieldType: 'IndividualField',
                        },
                        keys: ['items', 'quantity'],
                        groupFields: {
                            items: {
                                generalProperties: {
                                    isArray: false,
                                    isGroup: false,
                                    fieldApiName: 'items',
                                    fieldName: 'Item',
                                    fieldLabel: 'Item',
                                    fieldType: 'CodeableConceptField',
                                },
                                data: itemsRequiredOptions ?? [],
                            },
                            quantity: {
                                generalProperties: {
                                    isArray: false,
                                    isGroup: false,
                                    fieldApiName: 'quantity',
                                    fieldName: 'Quantity',
                                    fieldLabel: 'Quantity',
                                    fieldType: 'IndividualField',
                                    inputType: 'number',
                                },
                            },
                        },
                    } as any
                ];

                return this.resolveActivityDefinitionForServiceRequest(serviceRequest).pipe(
                    catchError(() => of(null))
                );
            })
        ).subscribe({
            next: (activityDefinition) => {
                this.resolvedActivityDefinition = activityDefinition;
                const extracted = extractItemsRequiredRows(activityDefinition);
                this.originalItemsRequiredRows = structuredClone(extracted ?? []);
                this.itemsRequiredRows = structuredClone(extracted ?? []);
                this.itemsRequiredDirty = false;
                this.itemsRequiredLoading = false;

                if (!activityDefinition) {
                    this.itemsRequiredReadOnlyNote = 'No Items Required configuration was found for this ServiceRequest code.';
                }
            },
            error: () => {
                this.itemsRequiredLoading = false;
                this.itemsRequiredReadOnlyNote = 'Failed to load Items Required configuration.';
            }
        });
    }

    private resolveActivityDefinitionForServiceRequest(serviceRequest: ServiceRequest): Observable<any | null> {
        const inst = (serviceRequest as any)?.instantiatesCanonical;
        const canonical = Array.isArray(inst) ? inst[0] : inst;
        const instanceId = this.parseIdFromCanonical(canonical, 'ActivityDefinition');

        if (instanceId) {
            this.itemsRequiredSource = 'instance';
            return this.http.get<any>(`${this.backendApiBaseUrl}/ActivityDefinition/${instanceId}`).pipe(
                catchError(() => of(null))
            );
        }

        const code = this.getServiceRequestCode(serviceRequest);
        if (!code) {
            this.itemsRequiredSource = 'none';
            return of(null);
        }

        this.itemsRequiredSource = 'default';
        return this.findActiveDefaultActivityDefinitionForCode$(code);
    }

    private findActiveDefaultActivityDefinitionForCode$(code: string): Observable<any | null> {
        const normalized = (code ?? '').toString().trim();
        if (!normalized) return of(null);

        const pickBest = (defs: any[]): any | null => {
            const matches = (defs || []).filter((d: any) => (d?.code as any)?.coding?.[0]?.code === normalized);
            const withTopic = matches.find((d: any) =>
                (d?.topic || []).some((t: any) => ((t?.text ?? '').toString().toLowerCase()).includes('items usage computation'))
            );
            return withTopic ?? matches[0] ?? null;
        };

        return this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
            resourceType: 'ActivityDefinition',
            urlSearchObject: {
                status: 'active',
            },
            querySearchObject: [
                { key: 'status', value: 'active' },
                { key: 'code.coding.code', value: normalized },
                { key: 'topic.text', value: 'items usage computation' }
            ],
            scope: 'orgWide',
            tier1FollowNextLinks: true,
            tier1MaxPages: 10,
            expectedMinLength: 1,
            tier3TriggerWhenBelowExpectedMinOnTier1Failure: true,
        }).pipe(
            map((fresh) => pickBest(fresh)),
            catchError(() => of(null))
        );
    }

    private getServiceRequestCode(sr: ServiceRequest): string | null {
        const code = (sr as any)?.code?.coding?.[0]?.code ?? (sr as any)?.code?.text ?? null;
        return code ? code.toString().trim() : null;
    }

    private parseIdFromCanonical(canonical: any, expectedType: string): string | null {
        if (!canonical || typeof canonical !== 'string') return null;
        const raw = canonical.toString().trim();
        if (!raw) return null;

        const noFrag = raw.split('#')[0].split('?')[0];
        const parts = noFrag.split('/').filter(Boolean);
        if (parts.length < 2) return null;

        const id = parts[parts.length - 1];
        const type = parts[parts.length - 2];
        if (type !== expectedType) return null;
        return id || null;
    }

    private buildNewActivityDefinitionFromBase(base: any, status: string): any {
        const next = structuredClone(base ?? {});
        if (next && typeof next === 'object') {
            delete next.id;
            delete next.meta;
        }
        next.resourceType = 'ActivityDefinition';
        next.status = status;
        if (!next.kind) next.kind = 'ServiceRequest';
        return next;
    }

    private activityDefinitionToCanonical(def: any): string | null {
        const id = (def?.id ?? '').toString().trim();
        if (!id) return null;
        const base = this.backendApiBaseUrl.replace(/\/+$/, '');
        return `${base}/ActivityDefinition/${id}`;
    }

    private extractCreatedActivityDefinitionCanonicalFromTransactionResponse(resp: any): string | null {
        const base = this.backendApiBaseUrl.replace(/\/+$/, '');

        const entries = Array.isArray(resp?.entry) ? resp.entry : [];
        for (const e of entries) {
            const resource = e?.resource;
            const id = (resource?.id ?? '').toString().trim();
            if (resource?.resourceType === 'ActivityDefinition' && id) {
                return `${base}/ActivityDefinition/${id}`;
            }
        }

        for (const e of entries) {
            const location = (e?.response?.location ?? e?.response?.header?.location ?? '').toString();
            if (!location) continue;
            const noFrag = location.split('#')[0].split('?')[0];
            const parts = noFrag.split('/').filter(Boolean);
            if (parts.length >= 2 && parts[parts.length - 2] === 'ActivityDefinition') {
                const id = parts[parts.length - 1];
                if (id) return `${base}/ActivityDefinition/${id}`;
            }
        }

        return null;
    }

    private persistItemsRequiredAsDefaultForCodeAndPinSr$(serviceRequest: ServiceRequest, code: string): Observable<any> {
        const srId = (serviceRequest as any)?.id?.toString?.() ?? '';
        if (!srId) return of(null);

        const baseForNew = this.resolvedActivityDefinition ?? {};

        return this.findActiveDefaultActivityDefinitionForCode$(code).pipe(
            switchMap((existingDefault) => {
                const newActiveBase = this.buildNewActivityDefinitionFromBase(baseForNew, 'active');
                const newActive = applyItemsRequiredRowsToActivityDefinition(newActiveBase, this.itemsRequiredRows, {
                    preserveUnitsFrom: existingDefault ?? baseForNew,
                    backendSystemFallback: this.backendApiBaseUrl
                });

                // Ensure code stays aligned to SR code
                const srCoding = (serviceRequest as any)?.code?.coding?.[0];
                if (srCoding?.code) {
                    (newActive as any).code = {
                        coding: [{ code: srCoding.code, display: srCoding.display, system: srCoding.system }],
                        text: (serviceRequest as any)?.code?.text ?? srCoding.display ?? srCoding.code
                    };
                } else if ((serviceRequest as any)?.code?.text) {
                    (newActive as any).code = { text: (serviceRequest as any).code.text };
                }

                const entries: any[] = [
                    { request: { method: 'POST', url: 'ActivityDefinition' }, resource: this.cleanFhirResourceForSubmit(newActive) }
                ];

                if (existingDefault?.id) {
                    entries.push({
                        request: { method: 'PUT', url: `ActivityDefinition/${existingDefault.id}` },
                        resource: this.cleanFhirResourceForSubmit({ ...existingDefault, status: 'retired' })
                    });
                }

                const bundle: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries } as any;
                return this.http.post<any>(this.backendApiBaseUrl, bundle, {
                    headers: {
                        'Content-Type': 'application/fhir+json',
                        Accept: 'application/fhir+json',
                        Prefer: 'return=representation'
                    }
                }).pipe(
                    tap((resp: any) => {
                        // Ensure org-wide state reflects created/retired ActivityDefinitions.
                        if (resp?.resourceType === 'Bundle') {
                            this.stateService.processOrgWideBundleTransaction(resp as any);
                        }
                    }),
                    map((resp: any) => this.extractCreatedActivityDefinitionCanonicalFromTransactionResponse(resp)),
                    switchMap((canonical) => {
                        if (!canonical) return of(null);
                        return this.overwriteServiceRequestInstantiatesCanonical$(srId, canonical);
                    }),
                    tap(() => {
                        // keep UI in sync for subsequent edits
                        this.itemsRequiredSource = 'instance';
                    })
                );
            })
        );
    }

    private persistItemsRequiredForInstanceOverride$(serviceRequest: ServiceRequest): Observable<any> {
        const srId = (serviceRequest as any)?.id?.toString?.() ?? '';
        if (!srId) return of(null);

        const baseForNew = this.resolvedActivityDefinition ?? {};
        const newRetiredBase = this.buildNewActivityDefinitionFromBase(baseForNew, 'retired');

        const newRetired = applyItemsRequiredRowsToActivityDefinition(newRetiredBase, this.itemsRequiredRows, {
            preserveUnitsFrom: baseForNew,
            backendSystemFallback: this.backendApiBaseUrl
        });

        // Ensure code stays aligned to SR code
        const srCoding = (serviceRequest as any)?.code?.coding?.[0];
        if (srCoding?.code) {
            (newRetired as any).code = { coding: [{ ...srCoding }] } as any;
        } else if ((serviceRequest as any)?.code?.text) {
            (newRetired as any).code = { text: (serviceRequest as any).code.text } as any;
        }

        const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

        const cleaned = this.cleanFhirResourceForSubmit(newRetired);

        return this.http.post<any>(`${this.backendApiBaseUrl}/ActivityDefinition`, cleaned, { headers }).pipe(
            tap((created: any) => {
                // Reflect created instance ActivityDefinition in org-wide state
                if (created?.resourceType) {
                    this.stateService.persistOrgWideResource(created as any, 'saved');
                }
            }),
            switchMap((created: any) => {
                const canonical = this.activityDefinitionToCanonical(created);
                if (!canonical) return of(null);
                return this.overwriteServiceRequestInstantiatesCanonical$(srId, canonical);
            })
        );
    }

    private overwriteServiceRequestInstantiatesCanonical$(serviceRequestId: string, canonical: string): Observable<any> {
        if (!serviceRequestId || !canonical) return of(null);
        const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

        return this.http.get<any>(`${this.backendApiBaseUrl}/ServiceRequest/${serviceRequestId}`, { headers: { Accept: 'application/fhir+json' } }).pipe(
            switchMap((sr: any) => {
                if (!sr || sr?.resourceType === 'OperationOutcome') return of(null);
                const next = { ...sr, instantiatesCanonical: [canonical] };
                return this.http.put<any>(`${this.backendApiBaseUrl}/ServiceRequest/${serviceRequestId}`, next, { headers }).pipe(
                    tap((updated: any) => this.persistServiceRequestState(updated))
                );
            })
        );
    }

    private cleanFhirResourceForSubmit<T>(resource: T): T {
        const cleaned = this.cleanValueForSubmit(resource) as any;
        return (cleaned === this.__DELETE_EMPTY ? resource : cleaned) as T;
    }

    private cleanValueForSubmit(value: any): any {
        if (value === null || value === undefined) return this.__DELETE_EMPTY;

        if (typeof value === 'string') {
            return value.trim() === '' ? this.__DELETE_EMPTY : value;
        }

        if (Array.isArray(value)) {
            const next = value
                .map((v) => this.cleanValueForSubmit(v))
                .filter((v) => v !== this.__DELETE_EMPTY);
            return next.length ? next : this.__DELETE_EMPTY;
        }

        if (typeof value === 'object') {
            const input = value as Record<string, any>;
            const out: Record<string, any> = {};

            for (const key of Object.keys(input)) {
                const cleaned = this.cleanValueForSubmit(input[key]);
                if (cleaned !== this.__DELETE_EMPTY) out[key] = cleaned;
            }

            // Preserve critical FHIR keys if present on the input
            if (input['resourceType'] && !out['resourceType']) out['resourceType'] = input['resourceType'];
            if (input['id'] && !out['id']) out['id'] = input['id'];

            return Object.keys(out).length ? out : this.__DELETE_EMPTY;
        }

        return value;
    }
}
